import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { moduleConfig } from './config';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../services/paginatedList';

export type MgaPeriodStatus = 'DRAFT' | 'OPEN' | 'CLOSED';
export type MgaColumnType = 'EARNING' | 'DEDUCTION' | 'INFO';
export type MgaCashEntryType = 'INCOME' | 'EXPENSE' | 'CTM';
export type MgaBondedSection = 'STOCK' | 'INCOMING' | 'EXPENSE';
export type MgaCrewRequestType = 'CASH_BY_MASTER' | 'ALLOTMENT' | 'SHEET_COLUMN';

type MgaApprovalUser = {
  id: number;
  userDisplayName?: string | null;
  userFirstName: string;
  userLastName: string;
};

export interface MgaCloseApprovalStep {
  id: number;
  stage: number;
  sequence: number;
  label?: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'SKIPPED';
  comments?: string | null;
  actedAt?: string | null;
  assigneeUser?: MgaApprovalUser | null;
  assigneeDepartment?: { id: number; name: string } | null;
  assigneeRank?: { id: number; name: string; abrv?: string | null } | null;
  actedByUser?: MgaApprovalUser | null;
}

export interface MgaCloseApproval {
  id: number;
  status: 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  title: string;
  summary?: string | null;
  createdAt: string;
  updatedAt?: string;
  submittedAt?: string | null;
  statusAt?: string | null;
  approvalType?: { id: number; code: string; name: string } | null;
  createdByUser?: MgaApprovalUser | null;
  masterApprovedByName?: string | null;
  lastReturn?: {
    comments?: string | null;
    at?: string | null;
    byUserId?: number | null;
    byUserName?: string | null;
    label?: string | null;
  } | null;
  actionHistory?: Array<{
    action: 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'RETURNED' | 'CANCELLED';
    at: string;
    byUserId: number;
    byUserName?: string | null;
    stage?: number | null;
    label?: string | null;
    comments?: string | null;
  }>;
  steps: MgaCloseApprovalStep[];
}

export type MgaSheetColumnBehavior = 'MANUAL' | 'CARRY_FORWARD' | 'DERIVED' | 'REQUEST_MIRROR';

export interface MgaSettlementColumnDef {
  id: number;
  code: string;
  name: string;
  columnType: MgaColumnType;
  dataType?: string;
  behavior: MgaSheetColumnBehavior;
  includeInTotal: boolean;
  sortOrder: number;
  isActive: boolean;
  showInGrid: boolean;
  sourceKey?: string | null;
  postingBucket?: string | null;
}
export interface MgaCrewLineSettlementAmount {
  id?: number;
  columnCode: string;
  columnName?: string;
  columnType: MgaColumnType;
  includeInTotal: boolean;
  periodAmount: number;
  columnId?: number | null;
}

export interface MgaColumnDef {
  id: number;
  code: string;
  name: string;
  columnType: MgaColumnType;
  includeInTotal: boolean;
  sortOrder: number;
  isActive: boolean;
}

export interface MgaCrewLineWageValue {
  id: number;
  mgaCrewLineId: number;
  columnId?: number | null;
  columnCode: string;
  columnName: string;
  columnType: MgaColumnType;
  includeInTotal: boolean;
  contractAmount: number;
  periodAmount: number;
}

export interface MgaCrewLine {
  id: number;
  mgaPeriodId: number;
  crewId: number;
  crewTransactionId: number;
  crewName: string;
  rankName?: string | null;
  /** CrewRank.sortOrder from the linked transaction (for sheet ordering). */
  rankSortOrder?: number;
  nationalityName?: string | null;
  signOnDate?: string | null;
  signOffDate?: string | null;
  contractTotalWage?: number | null;
  wageCurrency?: string | null;
  daysInPeriod: number;
  extraEarnings: number;
  previousBalance: number;
  overtimeHours: number;
  overtimeRate: number;
  cashAdvanceOffice: number;
  cashAdvanceMaster: number;
  allotmentAmount: number;
  cigarettesAmount: number;
  internetCardsAmount: number;
  otherBondedAmount: number;
  variousDeductions: number;
  requestedAllotment: number;
  requestedCashByMaster: number;
  remarks?: string | null;
  missingWageSplit: boolean;
  wageValues: MgaCrewLineWageValue[];
  settlementAmounts?: MgaCrewLineSettlementAmount[];
  otTotal?: number;
  grossPayable: number;
  totalDeductions?: number;
  deductions?: number;
  netDue: number;
}

export interface MgaPeriod {
  id: number;
  vesselId: number;
  periodFrom: string;
  periodTo: string;
  status: MgaPeriodStatus;
  currency?: string | null;
  notes?: string | null;
  closedAt?: string | null;
  closedByUserId?: number | null;
  masterApprovedByName?: string | null;
  masterApprovedAt?: string | null;
  closedByUser?: {
    id: number;
    userDisplayName?: string | null;
    userFirstName: string;
    userLastName: string;
  } | null;
  closeApproval?: MgaCloseApproval | null;
  accDocumentId?: number | null;
  accDocument?: {
    id: number;
    code?: string | null;
    status: string;
    description?: string | null;
    postingDate?: string | null;
  } | null;
  posting?: {
    status: 'skipped' | 'posted';
    reason?: string;
    documentId?: number;
    documentStatus?: string;
    documentCode?: string | null;
  };
  createdAt: string;
  updatedAt: string;
  vessel?: { id: number; vesselName: string; vesselAbrv?: string | null };
  lines?: MgaCrewLine[];
  columnDefs?: MgaColumnDef[];
  settlementColumnDefs?: MgaSettlementColumnDef[];
  deductionListColumns?: MgaSettlementColumnDef[];
  totals?: {
    grossPayable: number;
    netDue: number;
    extraEarnings: number;
    totalDeductions?: number;
    deductions?: number;
    previousBalance?: number;
    otTotal?: number;
    allotmentAmount?: number;
    lineCount: number;
  };
  /** List summary: cash income/CTM − expenses (period currency). */
  cashOnHand?: number;
  /** List summary: bonded stock/incoming − expense. */
  bondedBalance?: number;
  attachmentCount?: number;
  missingWageSplitCount?: number;
  closePending?: boolean;
  closeApprovalStatus?: 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED' | null;
  closeApprovalId?: number | null;
  closeApprovalStepsDone?: number;
  closeApprovalStepsTotal?: number;
  attachments?: MgaPeriodAttachment[];
  _count?: {
    lines: number;
    cashEntries?: number;
    bondedLines?: number;
    crewRequests?: number;
    attachments?: number;
  };
  generatedCount?: number;
}

export type MgaAttachmentKind = 'CLOSE_PACK' | 'CASH_VOUCHER' | 'BONDED_RECEIPT' | 'OTHER';

export interface MgaPeriodAttachment {
  id: number;
  kind?: MgaAttachmentKind | string;
  fileName: string;
  mimeType?: string;
  fileSize?: number;
  uploadedAt?: string;
}

export interface MgaCashEntry {
  id: number;
  mgaPeriodId: number;
  entryType: MgaCashEntryType;
  entryDate?: string | null;
  description: string;
  currency: string;
  amount: number;
  exchangeRate?: number;
  amountPeriod?: number;
  remarks?: string | null;
  mgaCrewLineId?: number | null;
  crewLine?: { id: number; crewName: string; rankName?: string | null } | null;
}

export interface MgaBondedLine {
  id: number;
  mgaPeriodId: number;
  section: MgaBondedSection;
  entryDate?: string | null;
  description: string;
  unitPrice: number;
  qty: number;
  amount: number;
  remarks?: string | null;
  sortOrder: number;
  mgaCrewLineId?: number | null;
  payrollColumnCode?: string | null;
  mgaCrewRequestId?: number | null;
  crewLine?: { id: number; crewName: string; rankName?: string | null } | null;
}

export interface MgaCrewRequest {
  id: number;
  mgaPeriodId: number;
  mgaCrewLineId: number;
  requestType: MgaCrewRequestType;
  columnCode?: string | null;
  entryDate?: string | null;
  amount: number;
  remarks?: string | null;
  mgaBondedLineId?: number | null;
  fromStockOut?: boolean;
  crewLine?: { id: number; crewName: string; rankName?: string | null } | null;
}

export const useMgaPeriods = (
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) => {
  const q = buildListQuery(searchParams, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<
    PaginatedApiResponse<MgaPeriod> | MgaPeriod[]
  >(`${moduleConfig.module.api.list}${q}`, fetcher, { revalidateOnFocus: true });
  const normalized = normalizePaginatedListResponse<MgaPeriod>(data, page, limit);
  return {
    data: normalized.data,
    clients: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useMgaPeriod = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<MgaPeriod>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, item: data, isLoading, isError: error, mutate };
};

export const useMgaCashEntries = (periodId: number) => {
  const { data, error, isLoading, mutate } = useSWR<MgaCashEntry[]>(
    periodId ? `/mga/periods/${periodId}/cash-entries` : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data: data ?? [], isLoading, isError: error, mutate };
};

export const useMgaBondedLines = (periodId: number) => {
  const { data, error, isLoading, mutate } = useSWR<MgaBondedLine[]>(
    periodId ? `/mga/periods/${periodId}/bonded-lines` : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data: data ?? [], isLoading, isError: error, mutate };
};

export const useMgaCrewRequests = (periodId: number) => {
  const { data, error, isLoading, mutate } = useSWR<MgaCrewRequest[]>(
    periodId ? `/mga/periods/${periodId}/crew-requests` : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data: data ?? [], isLoading, isError: error, mutate };
};

export interface MgaAuditEvent {
  id: number;
  mgaPeriodId: number;
  entityType: string;
  entityId?: number | null;
  action: string;
  summary: string;
  changes?: Record<string, unknown> | null;
  createdAt: string;
  userId?: number | null;
  userName?: string | null;
}

export const useMgaAuditEvents = (periodId: number) => {
  const { data, error, isLoading, mutate } = useSWR<MgaAuditEvent[]>(
    periodId ? `/mga/periods/${periodId}/audit-events` : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data: data ?? [], isLoading, isError: error, mutate };
};

export interface MgaRemittanceRow {
  crewName: string;
  rankName?: string | null;
  amount: number;
  currency?: string | null;
  entryDate?: string | null;
  remarks?: string | null;
  allotteeName?: string | null;
  bank?: string | null;
  bankAccount?: string | null;
  mgaCrewRequestId?: number;
}

export const useMgaRemittance = (periodId: number) => {
  const { data, error, isLoading, mutate } = useSWR<{
    periodId: number;
    currency?: string | null;
    rows: MgaRemittanceRow[];
  }>(periodId ? `/mga/periods/${periodId}/remittance` : null, fetcher, {
    revalidateOnFocus: true,
  });
  return { data: data?.rows ?? [], currency: data?.currency, isLoading, isError: error, mutate };
};

export const useMgaPayslip = (periodId: number, lineId: number) => {
  const { data, error, isLoading, mutate } = useSWR<{
    period: Pick<
      MgaPeriod,
      'id' | 'vesselId' | 'vessel' | 'periodFrom' | 'periodTo' | 'status' | 'currency'
    >;
    line: MgaCrewLine;
    columnDefs?: MgaColumnDef[];
    managementCompany?: {
      id: number;
      name: string;
      logoStorageKey?: string | null;
    } | null;
  }>(periodId && lineId ? `/mga/periods/${periodId}/lines/${lineId}` : null, fetcher, {
    revalidateOnFocus: true,
  });
  return { data, isLoading, isError: error, mutate };
};
